import { notificationService, restClient } from '@/Core'
import { TfiImport } from 'react-icons/tfi'
import { TbTrendingUp } from 'react-icons/tb'

import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'
import { renderDateTimeView, renderDateView } from '@/Ship/Helpers/fieldViews'

import { TopMenu } from '../Components/TopMenu'
import { CurrencyRateImportHint } from '../Components/CurrencyRateImportHint'
import { serviceFields } from '../Contracts/DictionaryFields'
import { CheckDictionaryPageAccessTask } from '../Tasks/CheckDictionaryPageAccessTask'

import type { IResourceConfig } from '@/Core/Contracts/UI'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

const { id, createdAt, createdBy, updatedBy } = serviceFields() as Record<string, IFieldConfig>

/**
 * Довідник «Курси валют» — Ship-сутність ядра, курси свого тенанта
 * (`SPEC-007` §5.6, R-27). Поля й підписи — легасі (`ListCurrencyRateResource.js`):
 * «Дата», а не «Дата курсу» PD.
 *
 * - Окремої «дати оновлення» в курсі немає (R-25): «Дата оновлення» — це
 *   `updatedAt`, лише читання; найсвіжіший курс ядро бере за датою курсу.
 * - Номінал і курс бек округлює до 4 знаків (R-26).
 * - «Імпорт курсів валют» ставить у чергу імпорт НБУ поточного тенанта;
 *   кнопка — лише з правом створення курсу, тобто адміністратору (R-29).
 */
export const ListCurrencyRateResource: IResourceConfig = {
    title: 'Курси валют',
    fields: {
        denominationCurrency: {
            label: 'Валюта номіналу',
            type: 'select',
            resource: 'Setting.ListCurrency',
            isRequired: true,
        },
        denomination: {
            label: 'Номінал',
            type: 'number',
            defaultValue: 1,
        },
        currency: {
            label: 'Валюта',
            type: 'select',
            resource: 'Setting.ListCurrency',
            isRequired: true,
        },
        rate: {
            label: 'Курс',
            type: 'number',
            defaultValue: 1,
        },
        date: {
            label: 'Дата',
            type: 'date',
            isRequired: true,
            // Власний показ: ядровий повторно розбирає вже відформатоване «14.10.2026»
            view: renderDateView,
        },
        ...(id ? { id } : {}),
        ...(createdAt ? { createdAt } : {}),
        ...(createdBy ? { createdBy } : {}),
        updatedAt: {
            label: 'Дата оновлення',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            view: renderDateTimeView,
        },
        ...(updatedBy ? { updatedBy } : {}),
    },
    headerPage: {
        eventHandlers: {
            onButtons: {
                import: ({ buttons }: { buttons: Record<string, unknown> }) => {
                    // Той самий вхід, що й у бека: ручний імпорт — право
                    // створення курсу. Без права кнопки немає, а не 403 по кліку
                    if (!CheckGlobalResourceAccessTask('Setting.ListCurrencyRate', 'create')) return

                    buttons['import'] = {
                        label: 'Імпорт курсів валют',
                        icon: <TfiImport />,
                        onClick: () => {
                            notificationService.showLoader('Імпорт курсів валют...')

                            const promise = restClient.get('/services/import/currency_rates')

                            if (!promise) {
                                notificationService.hideLoader()
                                notificationService.showError('Помилка запиту', 'Не вдалося створити запит')
                                return
                            }

                            promise
                                .then(() => {
                                    notificationService.showSuccess(
                                        'Імпорт курсів валют запущено',
                                        'Курси валют будуть імпортовані у фоновому режимі.'
                                    )
                                })
                                .catch(() => {
                                    notificationService.showError(
                                        'Помилка імпорту',
                                        'Не вдалося запустити імпорт курсів валют'
                                    )
                                })
                                .finally(() => {
                                    notificationService.hideLoader()
                                })
                        },
                    }
                },
            },
        },
    },
    // Колонки легасі (`SPEC-007` §7.4)
    table: {
        showColumns: ['denominationCurrency', 'denomination', 'currency', 'rate', 'date', 'updatedAt'],
    },
    // Фільтри легасі: Ід · Валюта
    filter: {
        includeFields: ['id', 'currency'],
    },
    places: {
        top: (
            <>
                <TopMenu current="currency-rates" />
                <CurrencyRateImportHint />
            </>
        ),
    },
    restUrl: '/services/currency_rates',
    pageUrl: '/settings/currency-rates',
    pageConfig: {
        title: 'Курси валют',
        icon: <TbTrendingUp />,
        accessFn: CheckDictionaryPageAccessTask,
    },
    settings: {
        searchFields: ['id', 'date'],
    },
}
